// Ajustar a janela de recorte depois de traçada.
//
// A parte que interessa testar é esta: dado um canto (ou um lado, ou a
// caixa inteira) e o sítio para onde o rato foi, qual é a caixa nova.
// Fica separada do desenho porque um erro aqui não se vê num teste de
// render, vê-se num recorte que salta para o sítio errado.

export interface CropRect {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** As pegas: quatro cantos, quatro lados, e a caixa inteira.
 *
 *  Os lados existem porque quase sempre o que se quer é alargar UM
 *  lado. Com cantos apenas, alargar à direita obrigava a mexer também
 *  em cima ou em baixo, e a caixa acabava sempre torta. */
export type CropHandle =
  | 'nw'
  | 'ne'
  | 'sw'
  | 'se'
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'move'

export type Point2 = [number, number]

/** Menor lado admissível, em unidades do desenho (mm). Abaixo disto a
 *  janela deixa de ter área e o desenho sairia vazio. */
export const MIN_CROP_SPAN = 1

/** Onde fica cada pega, em coordenadas do desenho. */
export function cropHandlePoints(
  c: CropRect,
): Array<{ id: CropHandle; x: number; y: number }> {
  const mx = (c.minX + c.maxX) / 2
  const my = (c.minY + c.maxY) / 2
  return [
    { id: 'nw', x: c.minX, y: c.minY },
    { id: 'ne', x: c.maxX, y: c.minY },
    { id: 'sw', x: c.minX, y: c.maxY },
    { id: 'se', x: c.maxX, y: c.maxY },
    { id: 'n', x: mx, y: c.minY },
    { id: 's', x: mx, y: c.maxY },
    { id: 'w', x: c.minX, y: my },
    { id: 'e', x: c.maxX, y: my },
  ]
}

/** O cursor que descreve o que aquela pega faz. */
export function cropHandleCursor(id: CropHandle): string {
  switch (id) {
    case 'nw':
    case 'se':
      return 'nwse-resize'
    case 'ne':
    case 'sw':
      return 'nesw-resize'
    case 'n':
    case 's':
      return 'ns-resize'
    case 'e':
    case 'w':
      return 'ew-resize'
    case 'move':
      return 'move'
  }
}

/** Ordena os limites. Arrastar um canto para lá do oposto é legítimo
 *  (a caixa vira do avesso); o que não pode é ficar gravada com o
 *  mínimo maior que o máximo. */
export function normalizeCrop(c: CropRect): CropRect {
  return {
    minX: Math.min(c.minX, c.maxX),
    maxX: Math.max(c.minX, c.maxX),
    minY: Math.min(c.minY, c.maxY),
    maxY: Math.max(c.minY, c.maxY),
  }
}

/** A janela tem área suficiente para valer a pena gravar. */
export function cropIsUsable(c: CropRect): boolean {
  const n = normalizeCrop(c)
  return n.maxX - n.minX >= MIN_CROP_SPAN && n.maxY - n.minY >= MIN_CROP_SPAN
}

/** A caixa depois de arrastar `handle` de `from` para `to`.
 *
 *  `inicial` é a caixa no momento em que se carregou, e não a do frame
 *  anterior: acumular deltas frame a frame arrasta erro e faz a caixa
 *  fugir do cursor. */
export function applyCropDrag(
  inicial: CropRect,
  handle: CropHandle,
  from: Point2,
  to: Point2,
): CropRect {
  if (handle === 'move') {
    const dx = to[0] - from[0]
    const dy = to[1] - from[1]
    return {
      minX: inicial.minX + dx,
      maxX: inicial.maxX + dx,
      minY: inicial.minY + dy,
      maxY: inicial.maxY + dy,
    }
  }
  const out = { ...inicial }
  // Cada letra do nome da pega diz um limite que ela manda. As que não
  // aparecem no nome ficam onde estavam, e é isso que faz um lado
  // alargar sozinho.
  if (handle.includes('w')) out.minX = to[0]
  if (handle.includes('e')) out.maxX = to[0]
  if (handle.includes('n')) out.minY = to[1]
  if (handle.includes('s')) out.maxY = to[1]
  return out
}
